import React, { useEffect, useState } from "react";
import { apiRequest } from "../../api/client";
import { EyeIcon, EyeOffIcon } from "./PasswordEyeIcons";

export default function ChangePasswordOverlay({ open, onClose }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCurrentPassword("");
    setPassword("");
    setPasswordConfirmation("");
    setShowCurrent(false);
    setShowPassword(false);
    setShowConfirm(false);
    setError("");
    setFieldErrors({});
    setSuccess(false);
  }, [open]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    if (!open) return undefined;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    const nextErrors = {};
    if (!currentPassword) nextErrors.currentPassword = "Current password is required.";
    if (!password) nextErrors.password = "New password is required.";
    if (!passwordConfirmation) {
      nextErrors.passwordConfirmation = "Confirm password is required.";
    } else if (password && password !== passwordConfirmation) {
      nextErrors.passwordConfirmation = "Passwords do not match.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    setLoading(true);
    try {
      const res = await apiRequest("api/change_password", {
        method: "POST",
        body: {
          current_password: currentPassword,
          password,
          password_confirmation: passwordConfirmation,
        },
      });

      if (res.ok) {
        setSuccess(true);
      } else {
        const msg = Array.isArray(res.data?.errors)
          ? res.data.errors.join("\n")
          : res.data?.message || "Failed to change password.";
        setError(msg);
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
        <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white shadow-xl">
          {success ? (
            <div className="px-6 pb-6 pt-8">
              <div className="flex flex-col items-center text-center">
                <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full border-4 border-emerald-500">
                  <span className="text-3xl text-emerald-500">✔</span>
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900">
                  Password Changed
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Your password has been updated successfully.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-8 w-full rounded-sm bg-[#4F46E5] py-3 text-sm font-semibold text-white hover:bg-[#4F46E5]"
                >
                  Done <span className="ml-4">→</span>
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative flex items-center justify-center px-6 pt-5">
                <p className="text-2xl font-extrabold text-slate-900">
                  Change Password
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="absolute right-6 rounded-full p-2 text-slate-500 hover:bg-slate-100"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4 px-6 pb-6 pt-4">
                {error && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 whitespace-pre-line">
                    {error}
                  </div>
                )}

                <div>
                  <div className="relative">
                    <input
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                      placeholder="Current Password"
                      type={showCurrent ? "text" : "password"}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400"
                      aria-label={showCurrent ? "Hide" : "Show"}
                    >
                      {showCurrent ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {fieldErrors.currentPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      {fieldErrors.currentPassword}
                    </p>
                  )}
                </div>

                <div>
                  <div className="relative">
                    <input
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                      placeholder="New Password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400"
                      aria-label={showPassword ? "Hide" : "Show"}
                    >
                      {showPassword ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {fieldErrors.password && (
                    <p className="mt-1 text-xs text-red-600">
                      {fieldErrors.password}
                    </p>
                  )}
                </div>

                <div>
                  <div className="relative">
                    <input
                      value={passwordConfirmation}
                      onChange={(e) => setPasswordConfirmation(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 pr-10 text-sm text-slate-800 shadow-sm focus:border-indigo-500 focus:outline-none"
                      placeholder="Confirm New Password"
                      type={showConfirm ? "text" : "password"}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400"
                      aria-label={showConfirm ? "Hide" : "Show"}
                    >
                      {showConfirm ? <EyeIcon className="h-5 w-5" /> : <EyeOffIcon className="h-5 w-5" />}
                    </button>
                  </div>
                  {fieldErrors.passwordConfirmation && (
                    <p className="mt-1 text-xs text-red-600">
                      {fieldErrors.passwordConfirmation}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-sm bg-[#4F46E5] py-3 text-sm font-semibold text-white hover:bg-[#4F46E5] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? "Please wait..." : "Change Password →"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
